import { formatBhojanPrice } from "./bhojan";

export interface GaushalaProgram {
  id: string | number;
  name: string;
  badge?: string;
  description?: string;
  price: string;
  period?: string;
  is_highlighted?: boolean;
  features: string[];
}

export type GaushalaSponsorshipSelection =
  | {
      kind: "program";
      id: string | number;
      title: string;
      description?: string;
      price: string;
      categoryLabel?: string;
      badgeText?: string;
      period?: string;
    }
  | {
      kind: "custom";
      title: string;
      description?: string;
      price: string;
      categoryLabel?: string;
      badgeText?: string;
      period?: string;
    };

export const GAUSHALA_SPONSORSHIP_PROGRAMS: GaushalaProgram[] = [
  {
    id: "daily-fodder",
    name: "Daily Fodder Support",
    badge: "Daily Support",
    description:
      "Provide fresh green grass, dry fodder, and organic nutrients for all cows for 1 day.",
    price: "1100",
    period: "Per Day",
    features: [
      "Fresh green fodder & clover mix",
      "Nutritious dry fodder & mineral supplement",
      "Clean drinking water throughout the day",
      "Dedicated Gau Sevak care",
    ],
  },
  {
    id: "1-year-fodder",
    name: "1 Year Fodder Sponsorship",
    badge: "1 Year Care",
    description:
      "Complete nutrition, green fodder, and health care for 1 full year.",
    price: "3500",
    period: "1 Year",
    features: [
      "Full 1 year green & dry fodder supply",
      "Regular veterinary checkups & vaccination",
      "Safe and hygienic shelter space",
      "80G tax exemption receipt provided",
    ],
  },
  {
    id: "2-year-fodder",
    name: "2 Year Fodder Sponsorship",
    badge: "2 Years Care",
    description:
      "Comprehensive care, nourishment, and medical protection for 2 years.",
    price: "7200",
    period: "2 Years",
    features: [
      "2 full years of fodder & nutrition",
      "Complete medical & vaccination coverage",
      "Special organic supplements & treats",
      "Donor certificate & quarterly updates",
    ],
  },
  {
    id: "5-year-fodder",
    name: "5 Year Fodder Sponsorship",
    badge: "5 Years Care",
    is_highlighted: true,
    description:
      "Provide long-term protection, nutritious feed, and shelter for 5 years.",
    price: "18000",
    period: "5 Years",
    features: [
      "5 years of uninterrupted Gau Seva & fodder",
      "Priority medical clinic & isolation support",
      "Special fodder distribution on chosen occasion",
      "Permanent donor recognition wall inclusion",
    ],
  },
  {
    id: "10-year-fodder",
    name: "10 Year Fodder Sponsorship",
    badge: "Decade Seva",
    is_highlighted: true,
    description:
      "A decade of lifelong protection, organic feed, and dedicated care.",
    price: "35000",
    period: "10 Years",
    features: [
      "10 years of complete food, shelter & vet care",
      "Special Puja & Gau Aarti on your birthday/anniversary",
      "Full family invitation for annual Gaushala Mahotsav",
      "Framed Gau Seva Samman certificate",
    ],
  },
  {
    id: "development-fund",
    name: "Gaushala Development Fund",
    badge: "Infrastructure",
    description:
      "Contribute towards shed expansion, water storage, and veterinary clinic facilities.",
    price: "9000",
    period: "One-time",
    features: [
      "Supports shed construction & ventilation",
      "Clean water storage & trough upgrades",
      "Veterinary clinic equipment & medicine stock",
      "Helps increase shelter capacity for rescued cows",
    ],
  },
];

export function formatGaushalaPrice(price?: string | number | null): string {
  return formatBhojanPrice(price);
}

export interface GaushalaScheme {
  id: number;
  title: string;
  short_title?: string;
  duration_type?: string;
  duration?: number;
  amount: string | number;
  per_day_amount?: string | number;
  description?: string;
  button_text?: string;
  features?: string[];
  is_highlighted?: boolean;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export type GaushalaSchemePayload = Pick<
  GaushalaScheme,
  | "title"
  | "short_title"
  | "duration_type"
  | "duration"
  | "amount"
  | "per_day_amount"
  | "description"
  | "button_text"
  | "features"
  | "is_highlighted"
  | "is_active"
  | "display_order"
>;

export function toGaushalaFeatures(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(/\n|,/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
}

export const GAUSHALA_DONATION_STATUS_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export type GaushalaDonationStatus =
  (typeof GAUSHALA_DONATION_STATUS_OPTIONS)[number]["value"];

export interface GaushalaDonationPayment {
  id: number;
  razorpay_order_id?: string;
  razorpay_payment_id?: string | null;
  amount: string;
  currency?: string;
  status?: string;
  payment_method?: string | null;
  payment_method_details?: unknown;
  receipt_no?: string | null;
  created_at?: string;
}

export interface GaushalaDonation {
  id: number;
  scheme?: number | string | GaushalaScheme | null;
  scheme_id?: number | null;
  scheme_title?: string | null;
  scheme_name?: string | null;
  donor_name?: string | null;
  name?: string | null;
  mobile_number?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  city?: string | null;
  full_address?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  occasion?: string | null;
  preferred_tithi?: string | null;
  preferred_sponsorship_date?: string | null;
  display_name?: string | null;
  special_instructions?: string | null;
  message?: string | null;
  is_anonymous?: boolean;
  info_confirmed?: boolean;
  contact_consent?: boolean;
  amount?: string | number | null;
  price?: string | number | null;
  payment_mode?: string | null;
  mode?: string | null;
  status?: string | null;
  payment_status?: string | null;
  receipt_no?: string | null;
  receipt_number?: string | null;
  payments?: GaushalaDonationPayment[];
  created_at?: string;
  updated_at?: string;
  donation_date?: string;
}

const GAUSHALA_PAYMENT_MODE_LABELS: Record<string, string> = {
  neft: "NEFT",
  rtgs: "RTGS",
  imps: "IMPS",
  upi: "UPI",
  cheque: "Cheque",
  cash: "Cash",
  wallet: "Wallet",
  card: "Card",
  netbanking: "Netbanking",
  online: "Online",
  offline: "Offline",
};

export function getGaushalaDonationDonorName(
  row: Pick<
    GaushalaDonation,
    "donor_name" | "name" | "display_name" | "is_anonymous"
  >
): string {
  if (row.is_anonymous) {
    return "Anonymous";
  }
  return (
    row.donor_name?.trim() ??
    row.name?.trim() ??
    row.display_name?.trim() ??
    "—"
  );
}

export function getGaushalaDonationPhone(
  row: Pick<GaushalaDonation, "mobile_number" | "phone" | "mobile">
): string {
  return (
    row.mobile_number?.trim() ?? row.phone?.trim() ?? row.mobile?.trim() ?? "—"
  );
}

export function getGaushalaDonationSchemeLabel(
  row: Pick<GaushalaDonation, "scheme_title" | "scheme_name" | "scheme">
): string {
  if (typeof row.scheme === "object" && row.scheme !== null) {
    return row.scheme.title || "—";
  }
  return row.scheme_title?.trim() ?? row.scheme_name?.trim() ?? "—";
}

export function getGaushalaDonationAmount(
  row: Pick<GaushalaDonation, "amount" | "price">
): number {
  return Number(row.amount ?? row.price ?? 0);
}

export function getGaushalaDonationStatus(row: GaushalaDonation): string {
  if (row.payments?.length) {
    return row.payments[0].status ?? row.status ?? row.payment_status ?? "—";
  }
  return row.status ?? row.payment_status ?? "—";
}

export function getGaushalaDonationReceiptNo(row: GaushalaDonation): string {
  const payment = row.payments?.[0];
  return String(
    row.receipt_no ??
      row.receipt_number ??
      payment?.receipt_no ??
      payment?.razorpay_payment_id ??
      (row.id ? `GSH-${row.id}` : "—")
  );
}

export function getGaushalaDonationPaymentMode(row: GaushalaDonation): string {
  const raw =
    row.payment_mode?.trim() ?? row.payments?.[0]?.payment_method?.trim() ?? "";
  if (!raw) {
    return "—";
  }
  const key = raw.toLowerCase();
  return (
    GAUSHALA_PAYMENT_MODE_LABELS[key] ??
    raw.charAt(0).toUpperCase() + raw.slice(1)
  );
}

export function buildGaushalaDonationAddress(row: GaushalaDonation): string {
  return [row.full_address, row.city, row.state, row.pincode, row.country]
    .map(part => (part !== null ? String(part).trim() : ""))
    .filter(Boolean)
    .join(", ");
}

export function normalizeGaushalaDonation(
  row: GaushalaDonation
): GaushalaDonation {
  return {
    ...row,
    donor_name: row.donor_name ?? row.name ?? undefined,
    phone: row.mobile_number ?? row.phone ?? row.mobile ?? undefined,
    mobile_number: row.mobile_number ?? row.phone ?? row.mobile ?? undefined,
    amount: row.amount ?? row.price,
    scheme_title: getGaushalaDonationSchemeLabel(row),
    receipt_no: getGaushalaDonationReceiptNo(row),
    status: getGaushalaDonationStatus(row),
    preferred_tithi: row.preferred_tithi?.trim() ?? undefined,
    payment_mode: (() => {
      const label = getGaushalaDonationPaymentMode(row);
      return label === "—" ? undefined : label;
    })(),
  };
}

export const GAUSHALA_OCCASION_OPTIONS = [
  "Birthday",
  "Anniversary",
  "Memorial",
  "Festival",
  "Punya Tithi",
  "Other",
] as const;

export type GaushalaOccasion = (typeof GAUSHALA_OCCASION_OPTIONS)[number];

export const GAUSHALA_OFFLINE_PAYMENT_MODE_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
] as const;

export const DURATION_TYPE_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
  { value: "lifetime", label: "Lifetime" },
  { value: "one-time", label: "One-time" },
] as const;

export interface GaushalaAddDonationPayload {
  scheme_id?: number;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion?: string;
  display_name?: string;
  special_instructions?: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  amount: string;
  mode: "offline";
  payment_mode: string;
  payment_reference?: string;
}

export interface GaushalaCreateOrderPayload {
  scheme_id?: number;
  amount?: string;
  mode?: "online" | string;
  donor_name: string;
  preferred_sponsorship_date: string;
  preferred_tithi?: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion?: string;
  display_name?: string;
  special_instructions?: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
}

export interface GaushalaCreateOrderResult {
  order_id: string;
  amount: number;
  currency: string;
  key_id?: string;
  donation_id?: number;
}

export interface GaushalaVerifyPaymentPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface GaushalaVerifyPaymentResult {
  receipt_no?: string;
  message?: string;
}

export function parseGaushalaCreateOrderResponse(
  response: unknown
): GaushalaCreateOrderResult {
  const root = response as {
    order_id?: string;
    amount?: number;
    currency?: string;
    key_id?: string;
    donation_id?: number;
    data?: {
      order_id?: string;
      amount?: number;
      currency?: string;
      key_id?: string;
      donation_id?: number;
    };
  };

  if (root.order_id) {
    return {
      order_id: root.order_id,
      amount: root.amount ?? 0,
      currency: root.currency ?? "INR",
      key_id: root.key_id,
      donation_id: root.donation_id,
    };
  }

  return {
    order_id: root.data?.order_id ?? "",
    amount: root.data?.amount ?? 0,
    currency: root.data?.currency ?? "INR",
    key_id: root.data?.key_id,
    donation_id: root.data?.donation_id,
  };
}

export function parseGaushalaVerifyPaymentResponse(
  response: unknown
): GaushalaVerifyPaymentResult {
  const root = response as {
    receipt_no?: string;
    message?: string;
    data?: { receipt_no?: string; receipt_number?: string; message?: string };
  };
  return {
    receipt_no:
      root.receipt_no ?? root.data?.receipt_no ?? root.data?.receipt_number,
    message: root.message ?? root.data?.message,
  };
}
